import type { BillingHistoryDoc, FirmInvoiceDoc } from "../Services/Billing";
import type {
  InvoiceAuditConcept,
  InvoiceAuditFieldChange,
} from "../Services/InvoiceAuditLog";
import {
  getBillingCategoryLabel,
  resolveItemCategory,
} from "./billingCategoryUtils";

type BillingDocLike = BillingHistoryDoc | FirmInvoiceDoc;

type ItemLike = {
  code?: string;
  description?: string;
  amount?: number;
  quantity?: number;
  paid?: number;
};

function formatVal(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "∅";
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value).trim();
}

function isEqualScalar(a: unknown, b: unknown): boolean {
  if (typeof a === "number" || typeof b === "number") {
    return Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.005;
  }
  const av = a === null || a === undefined ? "" : String(a).trim();
  const bv = b === null || b === undefined ? "" : String(b).trim();
  return av === bv;
}

interface ScalarFieldDef {
  key: string;
  label: string;
}

function diffScalarFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  fields: ScalarFieldDef[],
): InvoiceAuditFieldChange[] {
  const changes: InvoiceAuditFieldChange[] = [];
  for (const { key, label } of fields) {
    const from = before[key];
    const to = after[key];
    if (!isEqualScalar(from, to)) {
      changes.push({ field: label, from: formatVal(from), to: formatVal(to) });
    }
  }
  return changes;
}

function groupItemsByCode(items: ItemLike[]): Map<string, ItemLike[]> {
  const map = new Map<string, ItemLike[]>();
  for (const item of items) {
    const code = String(item.code ?? "").trim();
    const key = code || `__no_code_${map.size}`;
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }
  return map;
}

function itemAuditRef(code: string, index: number, total: number): string {
  if (!code || code.startsWith("__no_code_")) {
    return `#${index + 1}`;
  }
  return total > 1 ? `${code}#${index + 1}` : code;
}

function diffItems(
  before: ItemLike[] | undefined,
  after: ItemLike[] | undefined,
): InvoiceAuditFieldChange[] {
  const beforeItems = Array.isArray(before) ? before : [];
  const afterItems = Array.isArray(after) ? after : [];
  const changes: InvoiceAuditFieldChange[] = [];

  const beforeByCode = groupItemsByCode(beforeItems);
  const afterByCode = groupItemsByCode(afterItems);
  const allCodes = new Set([...beforeByCode.keys(), ...afterByCode.keys()]);

  for (const code of allCodes) {
    const bList = beforeByCode.get(code) ?? [];
    const aList = afterByCode.get(code) ?? [];
    const max = Math.max(bList.length, aList.length);

    for (let i = 0; i < max; i++) {
      const b = bList[i];
      const a = aList[i];
      const ref = itemAuditRef(code, i, max);

      if (b && !a) {
        changes.push({
          field: `item[${ref}]`,
          from: formatVal(b.amount),
          to: "∅",
        });
        continue;
      }
      if (!b && a) {
        changes.push({
          field: `item[${ref}]`,
          from: "∅",
          to: formatVal(a.amount),
        });
        continue;
      }
      if (!b || !a) {
        continue;
      }

      // Note: `paid` is intentionally excluded — it is derived from payments, not
      // from the invoice-editing form, and the preview recomputes it on open.
      (["description", "amount", "quantity"] as const).forEach((prop) => {
        if (!isEqualScalar(b[prop], a[prop])) {
          changes.push({
            field: `item[${ref}].${prop}`,
            from: formatVal(b[prop]),
            to: formatVal(a[prop]),
          });
        }
      });
    }
  }

  return changes;
}

// Invoice edits only concern charges/metadata. Payment- and credit-derived
// fields (totalPaid, balance, creditApplied, and per-item `paid`) are excluded
// because the edit preview recomputes them on open, which produced spurious
// "changed" entries even when the user did not touch anything.
const INVOICE_SCALAR_FIELDS: ScalarFieldDef[] = [
  { key: "invoiceNumber", label: "invoiceNumber" },
  { key: "issueDate", label: "issueDate" },
  { key: "dueDate", label: "dueDate" },
  { key: "total", label: "total" },
  { key: "currency", label: "currency" },
  { key: "memo", label: "memo" },
  { key: "coverLetterBody", label: "coverLetterBody" },
];

const PAYMENT_SCALAR_FIELDS: ScalarFieldDef[] = [
  { key: "amount", label: "amount" },
  { key: "method", label: "method" },
  { key: "reference", label: "reference" },
  { key: "description", label: "description" },
  { key: "paymentDate", label: "paymentDate" },
  { key: "status", label: "status" },
  { key: "currency", label: "currency" },
  { key: "memo", label: "memo" },
];

export function buildInvoiceChanges(
  before: BillingDocLike | null | undefined,
  after: BillingDocLike | null | undefined,
): InvoiceAuditFieldChange[] {
  if (!before || !after) {
    return [];
  }
  const b = before as Record<string, unknown>;
  const a = after as Record<string, unknown>;
  return [
    ...diffScalarFields(b, a, INVOICE_SCALAR_FIELDS),
    ...diffItems(b.items as ItemLike[], a.items as ItemLike[]),
  ];
}

/** Snapshot when a payment updates an invoice (status + payment-derived totals). */
export function buildInvoicePaymentAppliedChanges(
  before: BillingDocLike | null | undefined,
  after: BillingDocLike | null | undefined,
): InvoiceAuditFieldChange[] {
  if (!before || !after) {
    return [];
  }
  const b = before as Record<string, unknown>;
  const a = after as Record<string, unknown>;
  const changes: InvoiceAuditFieldChange[] = [
    {
      field: "status",
      from: formatVal(b.status),
      to: formatVal(a.status),
    },
  ];
  changes.push(
    ...diffScalarFields(b, a, [
      { key: "totalPaid", label: "totalPaid" },
      { key: "balance", label: "balance" },
    ]),
  );
  return changes;
}

export function buildPaymentChanges(
  before: BillingDocLike | null | undefined,
  after: BillingDocLike | null | undefined,
): InvoiceAuditFieldChange[] {
  if (!before || !after) {
    return [];
  }
  return diffScalarFields(
    before as Record<string, unknown>,
    after as Record<string, unknown>,
    PAYMENT_SCALAR_FIELDS,
  );
}

function itemLineAmount(item: ItemLike): number {
  return (
    Math.round(
      (Number(item.amount) || 0) * (Number(item.quantity) || 1) * 100,
    ) / 100
  );
}

function invoiceItemConceptLabel(
  item: ItemLike,
  invoice: BillingDocLike,
): string {
  const category = resolveItemCategory(item, invoice);
  if (category) {
    return getBillingCategoryLabel(category);
  }
  if (item.code === "discount") {
    return "Member Discount";
  }
  const description = String(item.description ?? "").trim();
  if (description) {
    return description;
  }
  return String(item.code ?? "Item").trim() || "Item";
}

/** Initial field snapshot when an invoice is first created (from ∅ → value). */
export function buildInvoiceCreationChanges(
  invoice: BillingDocLike | null | undefined,
): InvoiceAuditFieldChange[] {
  if (!invoice) {
    return [];
  }
  const record = invoice as Record<string, unknown>;
  const changes: InvoiceAuditFieldChange[] = [];

  for (const { key, label } of INVOICE_SCALAR_FIELDS) {
    const value = record[key];
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      changes.push({ field: label, from: "∅", to: formatVal(value) });
    }
  }

  for (const { key, label } of [
    { key: "status", label: "status" },
    { key: "creditApplied", label: "creditApplied" },
  ]) {
    const value = record[key];
    if (key === "creditApplied" && (Number(value) || 0) <= 0) {
      continue;
    }
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      changes.push({ field: label, from: "∅", to: formatVal(value) });
    }
  }

  const context = invoice.context as
    | { kind?: string; fiscalYear?: number }
    | undefined;
  if (context?.kind) {
    changes.push({
      field: "contextKind",
      from: "∅",
      to: formatVal(context.kind),
    });
  }
  if (context?.fiscalYear != null) {
    changes.push({
      field: "fiscalYear",
      from: "∅",
      to: formatVal(context.fiscalYear),
    });
  }

  const items = Array.isArray(invoice.items) ? invoice.items : [];
  for (const [code, list] of groupItemsByCode(items)) {
    list.forEach((item, index) => {
      const lineAmount = itemLineAmount(item);
      if (Math.abs(lineAmount) < 0.005) {
        return;
      }
      const ref = itemAuditRef(code, index, list.length);
      changes.push({
        field: `item[${ref}]`,
        from: "∅",
        to: formatVal(lineAmount),
      });
    });
  }

  return changes;
}

/** Line-item concepts and amounts from an invoice (for create / snapshot logs). */
export function buildInvoiceConcepts(
  invoice: BillingDocLike | null | undefined,
): InvoiceAuditConcept[] {
  if (!invoice) {
    return [];
  }
  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const concepts: InvoiceAuditConcept[] = [];

  for (const item of items) {
    const lineAmount = itemLineAmount(item);
    if (Math.abs(lineAmount) < 0.005) {
      continue;
    }
    concepts.push({
      concept: invoiceItemConceptLabel(item, invoice),
      amount: Math.round(Math.abs(lineAmount) * 100) / 100,
    });
  }

  return concepts;
}

/** Compact human-readable summary of the changes for the `detail` field. */
export function summarizeChanges(changes: InvoiceAuditFieldChange[]): string {
  if (changes.length === 0) {
    return "no field changes";
  }
  return changes
    .map((c) => `${c.field}: ${c.from} → ${c.to}`)
    .join("; ");
}

type PaymentAllocationLike = {
  invoiceId?: string;
  amount?: number;
  category?: string;
};

type PaymentConceptSource = {
  allocations?: PaymentAllocationLike[];
  description?: string;
  code?: string;
  amount?: number;
};

/**
 * Builds the breakdown of concepts paid and their amounts from a payment doc.
 * Uses per-allocation categories when available, otherwise falls back to the
 * payment code/description (e.g. direct payments).
 */
export function buildPaymentConcepts(
  payment: PaymentConceptSource | null | undefined,
): InvoiceAuditConcept[] {
  if (!payment) {
    return [];
  }
  const allocations: PaymentAllocationLike[] = Array.isArray(payment.allocations)
    ? payment.allocations
    : [];

  const withCategory = allocations.filter(
    (a) => (Number(a.amount) || 0) > 0,
  );
  if (withCategory.length > 0) {
    return withCategory.map((a) => ({
      concept: (a.category || "Payment").trim() || "Payment",
      amount: Math.round((Number(a.amount) || 0) * 100) / 100,
    }));
  }

  const fallbackConcept =
    (payment.description || payment.code || "Payment").trim() || "Payment";
  return [
    {
      concept: fallbackConcept,
      amount: Math.round((Number(payment.amount) || 0) * 100) / 100,
    },
  ];
}

/** Compact human-readable summary of the concepts for the `detail` field. */
export function summarizeConcepts(concepts: InvoiceAuditConcept[]): string {
  if (concepts.length === 0) {
    return "";
  }
  return concepts
    .map((c) => `${c.concept}: ${c.amount.toFixed(2)}`)
    .join("; ");
}
